import { X } from 'lucide-react'
import {
  useEffect,
  useId,
  type ReactNode,
} from 'react'

import { cn } from '@/lib/utils'

import { Button } from './button'

export type OffcanvasProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  children: ReactNode
  className?: string
  footer?: ReactNode
}

export const Offcanvas = ({
  open,
  onOpenChange,
  title,
  children,
  className,
  footer,
}: OffcanvasProps) => {
  const titleId = useId()

  useEffect(() => {
    if (!open) {
      return
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onOpenChange(false)
      }
    }

    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open, onOpenChange])

  if (!open) {
    return null
  }

  return (
    <div className="fixed inset-0 z-50">
      <button
        type="button"
        aria-label="Close panel"
        className="absolute inset-0 bg-black/60"
        onClick={() => onOpenChange(false)}
      />

      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cn(
          'absolute inset-y-0 right-0 flex w-full max-w-md flex-col bg-background shadow-xl',
          className
        )}
      >
        <header className="flex items-center justify-between border-b border-outline/40 px-6 py-5">
          <h2 id={titleId} className="text-xl font-bold text-foreground">
            {title}
          </h2>

          <Button
            type="button"
            variant="secondary"
            className='px-2'
            size="sm"
            aria-label="Close"
            onClick={() => onOpenChange(false)}
          >
            <Button.Icon>
              <X className="size-4" />
            </Button.Icon>
          </Button>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-6">{children}</div>

        {footer ? (
          <footer className="border-t border-outline/40 px-6 py-5">{footer}</footer>
        ) : null}
      </aside>
    </div>
  )
}
